/**
 * The version matrix (plan.md step 54): pressing "© MyRave.io" in the footer
 * shows what is running — every component's version, the connection, when the
 * app last reached the server, and when this rave's offline copy last synced.
 *
 * **It opens with no network at all.** The web build's own versions are
 * compiled in (`lib/build.ts`); the server's are the last `/version` answer
 * kept on the device, labelled with its age; the rave's offline copy is read
 * from IndexedDB. The connection reads Offline until the server answers (the
 * user, 2026-10-06) and is probed again while the dialog is open.
 *
 * A bug report starts here, so the whole matrix copies as text.
 */
import { createPortal } from 'react-dom';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { MODULE_VERSIONS, readNativeApp } from '@myrave/shared';
import { Badge, Button, Dialog } from '@/ui';
import { useT, type Vars } from '@/i18n';
import { useLocale } from './LocaleProvider';
import { intlLocale } from './rich';
import { apiUrl, platformUrl } from '@/lib/tenant';
import { BUILD } from '@/lib/build';
import { lastReached, probeConnection, type Connection } from '@/lib/reach';
import { useCurrentRave } from '@/lib/currentRave';
import {
  fetchVersions, keptEditionVersion, keptServerVersions,
  type EditionVersion, type Kept, type ServerVersions,
} from '@/lib/versionInfo';
import { db } from '@/offline/db';
import { offlineCopyStatus, type OfflineCopyStatus } from '@/offline/bundle';
import { useReleaseLine } from '@/lib/releaseLines';
import { readJson } from '@/lib/storage';
import { WORKER_PROBLEM } from '@/offline/register';
import './VersionMatrix.css';

type Tr = (english: string, vars?: Vars) => string;

/** How often the open dialog asks again. */
const PROBE_EVERY_MS = 15_000;

function ago(at: number, tr: Tr): string {
  const mins = Math.floor((Date.now() - at) / 60_000);
  if (mins < 1) return tr('just now');
  if (mins < 60) return tr('{count} min ago', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return tr('{count} h ago', { count: hours });
  const days = Math.floor(hours / 24);
  return days === 1 ? tr('yesterday') : tr('{count} days ago', { count: days });
}

/** The service worker's shell cache is `myrave-shell-<content hash>`. */
async function shellHash(): Promise<string | null> {
  try {
    if (typeof caches === 'undefined') return null;
    const name = (await caches.keys()).find((k) => k.startsWith('myrave-shell-'));
    return name ? name.slice('myrave-shell-'.length) : null;
  } catch {
    return null;
  }
}

/**
 * The app itself, kept for no signal: is a service worker in charge of this page, and how many of
 * the app's own files its shell cache holds (the user, 2026-10-30: an iPhone opened to a black
 * screen with no signal — an iPhone's web view runs a worker only on app-bound domains).
 */
export interface OfflineApp { worker: 'unsupported' | 'off' | 'on'; files: number; problem?: string }
async function offlineApp(): Promise<OfflineApp> {
  const sw = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker;
  if (!sw) return { worker: 'unsupported', files: 0 };
  let files = 0;
  try {
    if (typeof caches !== 'undefined') {
      const name = (await caches.keys()).find((k) => k.startsWith('myrave-shell-'));
      if (name) files = (await (await caches.open(name)).keys()).length;
    }
  } catch { /* no cache to read: none kept */ }
  // Why not, as the browser said it when the worker was refused or dropped (`offline/register.ts`).
  const problem = sw.controller ? undefined : readJson<{ message?: string } | null>(null, WORKER_PROBLEM, null)?.message;
  return { worker: sw.controller ? 'on' : 'off', files, ...(problem ? { problem } : {}) };
}

/** How often the open dialog reads the download's progress again. */
const COPY_EVERY_MS = 2_000;

/** "© 2026 MyRave.io" — the footer's line, and the way into the matrix. */
export function VersionMatrixButton({ newsHref }: {
  /**
   * Where "What's new in MyRave" goes: the app's own page inside the app
   * shell (signed in), the public one from the public site.
   */
  newsHref?: string;
} = {}) {
  const tr = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        dir="ltr"
        className="version-matrix-link"
        aria-haspopup="dialog"
        title={tr('Versions')}
        onClick={() => setOpen(true)}
      >
        © {new Date().getFullYear()} MyRave.io
      </button>
      {/*
        * At the document's top, not beside the button: the button sits in the
        * menu now, and the menu slides with a transform — a fixed dialog
        * inside a transformed parent is confined to that parent's box.
        */}
      {open && createPortal(<VersionMatrix onClose={() => setOpen(false)} newsHref={newsHref} />, document.body)}
    </>
  );
}

interface Line { label: string; value: string; detail?: string }

export function VersionMatrix({ onClose, newsHref }: { onClose: () => void; newsHref?: string }) {
  const tr = useT();
  const { locale } = useLocale();
  const rave = useCurrentRave();
  const when = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleString(intlLocale(locale), { dateStyle: 'medium', timeStyle: 'short' }) : null;

  const [connection, setConnection] = useState<Connection>('offline');
  const [server, setServer] = useState<Kept<ServerVersions> | null>(() => keptServerVersions());
  const [reached, setReached] = useState<number | null>(() => lastReached());
  const [shell, setShell] = useState<string | null>(null);
  const [edition, setEdition] = useState<(EditionVersion & { fetchedAt: number }) | null>(
    () => (rave ? keptEditionVersion(rave.editionId) : null),
  );
  const [copySynced, setCopySynced] = useState<number | null>(null);
  const [offlineCopy, setOfflineCopy] = useState<OfflineCopyStatus | null>(null);
  const [app, setApp] = useState<OfflineApp | null>(null);
  const [copied, setCopied] = useState(false);
  const native = typeof navigator === 'undefined' ? null : readNativeApp(navigator.userAgent);

  useEffect(() => { void shellHash().then(setShell); void offlineApp().then(setApp); }, []);

  // This rave's photos and map on the phone — read again while the dialog is open, so a
  // download in progress moves (it writes its progress after every file).
  useEffect(() => {
    if (!rave) { setOfflineCopy(null); return undefined; }
    let live = true;
    const read = () => { void offlineCopyStatus(rave.editionId).then((s) => { if (live) setOfflineCopy(s); }); };
    read();
    const id = setInterval(read, COPY_EVERY_MS);
    return () => { live = false; clearInterval(id); };
  }, [rave]);

  // This rave's offline copy: when it last came from the server.
  useEffect(() => {
    if (!rave) return;
    setEdition(keptEditionVersion(rave.editionId));
    void db.bundles.get(rave.editionId).then((b) => setCopySynced(b?.fetchedAt ?? null)).catch(() => {});
  }, [rave]);

  // Probe now, every 15 s while open, and when the browser says the network
  // changed. Each time the server answers, ask it for its versions too.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const state = await probeConnection(apiUrl());
      if (cancelled) return;
      setConnection(state);
      setReached(lastReached());
      if (state === 'offline') return;
      const fresh = await fetchVersions(apiUrl(), rave?.editionId ?? null);
      if (cancelled || !fresh) return;
      setServer({ answer: fresh.server, fetchedAt: fresh.fetchedAt });
      setReached(lastReached());
      if (rave) setEdition(keptEditionVersion(rave.editionId));
    };
    void check();
    const id = setInterval(check, PROBE_EVERY_MS);
    const now = () => { void check(); };
    window.addEventListener('online', now);
    window.addEventListener('offline', now);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('online', now);
      window.removeEventListener('offline', now);
    };
  }, [rave]);

  const s = server?.answer;
  const notKnown = tr('Not known yet');
  const components: Line[] = [
    { label: tr('Platform'), value: s?.platform.version ?? BUILD.version },
    {
      label: tr('Web app'),
      value: `${BUILD.version} · ${BUILD.sha}${shell ? ` · ${shell}` : ''}`,
      ...(BUILD.builtAt ? { detail: tr('Built {date}', { date: when(BUILD.builtAt)! }) } : {}),
    },
    s
      ? { label: tr('API'), value: `${s.api.version} · ${s.api.sha}`, ...(s.api.deployedAt ? { detail: tr('Deployed {date}', { date: when(s.api.deployedAt)! }) } : {}) }
      : { label: tr('API'), value: notKnown },
    ...(native ? [{ label: tr('Phone app'), value: `${native.os} ${native.version ?? '?'}${native.build ? ` (${native.build})` : ''}` }] : []),
    { label: tr('Database'), value: s?.database.migration ?? notKnown },
  ];
  const raveLines: Line[] = rave ? [
    { label: tr('Content'), value: edition?.content ? tr('content v{n}', { n: edition.content }) : notKnown },
    { label: tr('Offline copy synced'), value: copySynced ? ago(copySynced, tr) : tr('Never') },
  ] : [];
  const status: Line[] = [
    { label: tr('Last reached the server'), value: reached ? ago(reached, tr) : tr('Never') },
    ...(server ? [{ label: tr('Server versions checked'), value: ago(server.fetchedAt, tr) }] : []),
  ];
  const number = (n: number, digits = 0) =>
    new Intl.NumberFormat(intlLocale(locale), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
  const mb = (bytes: number) => number(bytes / 1_000_000, 1);
  const copyLine: Line | null = !rave || !offlineCopy ? null
    : !offlineCopy.stored ? { label: tr('Offline copy'), value: tr('Not on this phone') }
    // A list with nothing in it is said in words, never a full bar over "0 of 0" (the 0.14.74 walk, both phones).
    : offlineCopy.total === 0 ? { label: tr('Offline copy'), value: tr('Nothing to download') }
    : offlineCopy.complete ? { label: tr('Offline copy'), value: tr('All downloaded'), detail: tr('Files: {done} of {total}', { done: number(offlineCopy.total), total: number(offlineCopy.total) }) + ' · ' + tr('{done} of {total} MB', { done: mb(offlineCopy.totalBytes), total: mb(offlineCopy.totalBytes) }) }
    : {
      label: tr('Offline copy'),
      value: tr('Files: {done} of {total}', { done: number(offlineCopy.done), total: number(offlineCopy.total) }),
      detail: tr('{done} of {total} MB', { done: mb(offlineCopy.bytes), total: mb(offlineCopy.totalBytes) }),
    };
  const appLine: Line | null = !app ? null : {
    label: tr('Offline app'),
    value: app.worker === 'unsupported' ? tr('Not supported here') : app.worker === 'on' && app.files > 0 ? tr('Kept for offline') : tr('Not kept for offline'),
    ...(app.worker === 'unsupported' ? {} : { detail: tr('Files kept: {count}', { count: number(app.files) }) + (app.problem ? ` · ${app.problem}` : '') }),
  };
  const connectionLabel = { online: tr('Online'), limited: tr('Limited'), offline: tr('Offline') }[connection];

  const copy = async () => {
    const lines = [
      ...components.map((l) => `${l.label}: ${l.value}${l.detail ? ` (${l.detail})` : ''}`),
      ...(s ? Object.entries(s.modules).map(([k, v]) => `  ${k}: ${v}`) : []),
      ...(rave ? [`${tr('This rave')}: ${rave.name} (${rave.editionId})`, ...raveLines.map((l) => `${l.label}: ${l.value}`)] : []),
      `${tr('Connection')}: ${connectionLabel}`,
      ...[copyLine, appLine].filter((l): l is Line => l !== null).map((l) => `${l.label}: ${l.value}${l.detail ? ` (${l.detail})` : ''}`),
      ...status.map((l) => `${l.label}: ${l.value}`),
      `UA: ${typeof navigator === 'undefined' ? '' : navigator.userAgent}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
    } catch {
      // No clipboard (an old web view, a denied permission): nothing to say
      // beyond the button not changing — the matrix is still on screen.
    }
  };

  return (
    <Dialog
      title={tr('Versions')}
      onClose={onClose}
      footer={(
        <>
          <a href={newsHref ?? `${platformUrl()}/whats-new`} className="version-matrix-news">{tr("What's new in MyRave")}</a>
          <Button variant="secondary" onClick={() => { void copy(); }}>{copied ? tr('Copied') : tr('Copy')}</Button>
        </>
      )}
    >
      <div className="version-matrix">
        <Section>
          <Row label={tr('Connection')} value={
            <Badge tone={connection === 'online' ? 'good' : connection === 'limited' ? 'warn' : 'bad'}>{connectionLabel}</Badge>
          } />
          {copyLine && offlineCopy && offlineCopy.total > 0 && <CopyRow line={copyLine} done={offlineCopy.complete ? 1 : offlineCopy.total ? offlineCopy.done / offlineCopy.total : 0} />}
          {copyLine && offlineCopy && offlineCopy.total === 0 && <Row label={copyLine.label} value={copyLine.value} />}
          {appLine && <Row {...appLine} />}
          {status.map((l) => <Row key={l.label} {...l} />)}
        </Section>

        <Section>
          {components.map((l) => <Row key={l.label} {...l} />)}
        </Section>

        {rave && (
          <Section title={tr('This rave')}>
            <Row label={rave.name} value={rave.editionId} />
            {raveLines.map((l) => <Row key={l.label} {...l} />)}
          </Section>
        )}

        <Section title={tr('Modules')}>
          <ModuleList modules={s?.modules ?? MODULE_VERSIONS} />
        </Section>
      </div>
    </Dialog>
  );
}

/**
 * Every module and its version — the server's when it has answered, else this
 * build's own (`MODULE_VERSIONS`), so the list is there offline too. A module
 * opens its history: the releases that changed it, from `releases/*.md`,
 * loaded on demand so the history is not in every first download.
 */
function ModuleList({ modules }: { modules: Readonly<Record<string, string>> }) {
  const tr = useT();
  const [open, setOpen] = useState<string | null>(null);
  const [history, setHistory] = useState<ModuleHistory | null>(null);
  const line = useReleaseLine();
  const toggle = (key: string) => {
    if (open === key) { setOpen(null); return; }
    setOpen(key);
    setHistory(null);
    void import('@/lib/releaseNotes').then((m) => setHistory(m.moduleHistory(key)));
  };
  return (
    <div className="version-matrix-modules" dir="ltr">
      {Object.entries(modules).map(([key, v]) => (
        <Fragment key={key}>
          <button
            type="button"
            className="version-matrix-module"
            aria-label={`${key} ${v}`}
            aria-expanded={open === key}
            onClick={() => toggle(key)}
          >
            <span className="version-matrix-module-k">{key}</span>
            <span className="version-matrix-module-v">{v}</span>
          </button>
          {open === key && (
            <section aria-label={`${key} history`} className="version-matrix-history">
              {!history ? null : history.length === 0 ? (
                <p className="version-matrix-detail">{tr('No release names this module yet.')}</p>
              ) : (
                <ol className="version-matrix-history-list">
                  {history.map((h) => (
                    <li key={h.version}>
                      <span className="version-matrix-history-v">{h.version}</span>
                      {h.date && <span className="version-matrix-detail"> · {h.date}</span>}
                      {h.lines.length > 0 ? (
                        <ul>{h.lines.map((l) => <li key={l.text}>{line(l.text)}</li>)}</ul>
                      ) : (
                        <p className="version-matrix-detail">{tr('Changed in this release.')}</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}
        </Fragment>
      ))}
    </div>
  );
}

type ModuleHistory = ReturnType<typeof import('@/lib/releaseNotes').moduleHistory>;

function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="version-matrix-section" aria-label={title}>
      {title && <h3 className="version-matrix-title">{title}</h3>}
      <dl className="version-matrix-list">{children}</dl>
    </section>
  );
}

/** The offline copy's row: its words, and under them a bar of how much is on the phone. */
function CopyRow({ line, done }: { line: Line; done: number }) {
  const pct = Math.round(Math.max(0, Math.min(1, done)) * 100);
  return (
    <div className="version-matrix-row version-matrix-copy">
      <dt>{line.label}</dt>
      <dd>
        <span className="version-matrix-value" dir="ltr">{line.value}</span>
        {line.detail && <span className="version-matrix-detail" dir="ltr">{line.detail}</span>}
        <span
          className="version-matrix-bar"
          role="progressbar"
          aria-label={line.label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-valuetext={line.detail ? `${line.value} · ${line.detail}` : line.value}
        >
          <span className="version-matrix-bar-fill" style={{ inlineSize: `${pct}%` }} />
        </span>
      </dd>
    </div>
  );
}

function Row({ label, value, detail }: { label: string; value: ReactNode; detail?: string }) {
  return (
    <div className="version-matrix-row">
      <dt>{label}</dt>
      <dd>
        <span className="version-matrix-value" dir="ltr">{value}</span>
        {detail && <span className="version-matrix-detail">{detail}</span>}
      </dd>
    </div>
  );
}
